import {PagedRequestDTO} from '../../common/models/PagedRequestDTO';
import {TableControl} from '../../common/models/TableControl';
import {createFieldMap, getSortDirection} from '../../common/util/Util';
import {PagedFilterDTO} from '../../common/models/PagedFilterDTO';
import {EntityTable} from '../../common/types/entity-table.enum';

export class ClmRequestBuilder {

  public static getFilteredListOfInsuredPersonsPagedRequestDTO(value: string) {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblInsuredPerson;
    pagedRequestDTO.pageNumber = 0;
    pagedRequestDTO.pageSize = 50;
    pagedRequestDTO.sortField = 'lastName';
    pagedRequestDTO.sortDirection = 'ASCENDING';
    pagedRequestDTO.fieldMap = createFieldMap('lastName', value);
    return pagedRequestDTO;
  }

  public static getFilteredListOfInsuredPersonsByIdNumberPagedRequestDTO(value: string) {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblInsuredPerson;
    pagedRequestDTO.pageNumber = 0;
    pagedRequestDTO.pageSize = 50;
    pagedRequestDTO.sortField = 'lastName';
    pagedRequestDTO.sortDirection = 'ASCENDING';
    pagedRequestDTO.fieldMap = createFieldMap('nationalIdNumber', value);
    return pagedRequestDTO;
  }

  public static getFilteredListOfPoliciesPagedRequestDTO(value: string) {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblPolicy;
    pagedRequestDTO.pageNumber = 0;
    pagedRequestDTO.pageSize = 50;
    pagedRequestDTO.sortField = 'number';
    pagedRequestDTO.sortDirection = 'ASCENDING';
    pagedRequestDTO.fieldMap = createFieldMap('number', value);
    return pagedRequestDTO;
  }

  public static getFilteredListOfCasesPagedRequestDTO(value: string) {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblCase;
    pagedRequestDTO.pageNumber = 0;
    pagedRequestDTO.pageSize = 50;
    pagedRequestDTO.sortField = 'name';
    pagedRequestDTO.sortDirection = 'ASCENDING';
    pagedRequestDTO.fieldMap = createFieldMap('name', value);
    return pagedRequestDTO;
  }

  public static getListOfLocationPagedRequestDTO(value: string) {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblLocation;
    pagedRequestDTO.pageNumber = 0;
    pagedRequestDTO.pageSize = 50;
    pagedRequestDTO.sortField = 'countryByCountryId.countryId';
    pagedRequestDTO.sortDirection = 'ASCENDING';
    pagedRequestDTO.fieldMap = createFieldMap('cityTown', value);
    return pagedRequestDTO;
  }

  public static getListOfCasesPagedRequestDTO(tableControl: TableControl) {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblCase;
    pagedRequestDTO.pageNumber = tableControl.pageNumber;
    pagedRequestDTO.pageSize = tableControl.pageSize;
    pagedRequestDTO.sortField = tableControl.sortField;
    pagedRequestDTO.sortDirection = getSortDirection(tableControl.sortDirection);

    // Note: 'filterField' to control table filter data by 'filterValue'
    if (tableControl.filterField) {
      pagedRequestDTO.fieldMap = createFieldMap(
        tableControl.filterField,
        tableControl.filterValue
      );
    }
    else {
      pagedRequestDTO.fieldMap = null;
    }
    return pagedRequestDTO;
  }

  public static getListOfDiagnosisByDescriptionPagedRequestDTO(value: string, pageSize = 50, primaryOnly = false) {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblDiagnosis;
    pagedRequestDTO.pageNumber = 0;
    pagedRequestDTO.pageSize = pageSize;

    const fieldMap: {} = createFieldMap(
      'type',
      'SA ICD-10 MIT'
    );

    let pagedFilterDTO = new PagedFilterDTO();
    pagedFilterDTO.name = 'description';
    pagedFilterDTO.value = value;
    fieldMap[pagedFilterDTO.name] = pagedFilterDTO;

    if (primaryOnly) {
      pagedFilterDTO = new PagedFilterDTO();
      pagedFilterDTO.name = 'validIcd10Primary';
      pagedFilterDTO.value = 'Y';
      fieldMap[pagedFilterDTO.name] = pagedFilterDTO;
    }

    pagedRequestDTO.fieldMap = fieldMap;
    return pagedRequestDTO;
  }

  public static getListOfDiagnosisByCodePagedRequestDTO(value: string, pageSize = 50, primaryOnly = false) {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblDiagnosis;
    pagedRequestDTO.pageNumber = 0;
    pagedRequestDTO.pageSize = pageSize;

    const fieldMap: {} = createFieldMap(
      'type',
      'SA ICD-10 MIT'
    );

    let pagedFilterDTO = new PagedFilterDTO();
    pagedFilterDTO.name = 'code';
    pagedFilterDTO.value = value;
    fieldMap[pagedFilterDTO.name] = pagedFilterDTO;

    if (primaryOnly) {
      pagedFilterDTO = new PagedFilterDTO();
      pagedFilterDTO.name = 'validIcd10Primary';
      pagedFilterDTO.value = 'Y';
      fieldMap[pagedFilterDTO.name] = pagedFilterDTO;
    }

    pagedRequestDTO.fieldMap = fieldMap;
    return pagedRequestDTO;
  }

  public static getTblProvidersPagedRequestDTO({
                                                 filterField,
                                                 filterValue,
                                                 sortField = null,
                                                 sortDirection = 'ASCENDING'
                                               }
  ) {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblProvider;
    pagedRequestDTO.pageNumber = 0;
    pagedRequestDTO.pageSize = 50;
    pagedRequestDTO.sortField = sortField;
    pagedRequestDTO.sortDirection = sortDirection;
    pagedRequestDTO.fieldMap = createFieldMap(filterField, filterValue);
    return pagedRequestDTO;
  }

  public static buildBasicPagedRequestDTO(entityClass: string): PagedRequestDTO {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = entityClass;
    pagedRequestDTO.pageNumber = 0;
    pagedRequestDTO.pageSize = 50;
    pagedRequestDTO.sortField = 'businessTypeByBusinessTypeId.name';
    pagedRequestDTO.sortDirection = 'ASCENDING';
    pagedRequestDTO.fieldMap = {};

    return pagedRequestDTO;
  }

  public static getListOfCodingSchemaByDescriptionPagedRequestDTO(value: string, procedureSchema: string, pageSize = 50) {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblCodingSchema;
    pagedRequestDTO.pageNumber = 0;
    pagedRequestDTO.pageSize = pageSize;

    const fieldMap: {} = createFieldMap(
      'shortDescription',
      value
    );

    const pagedFilterDTO = new PagedFilterDTO();
    pagedFilterDTO.name = 'codingSchemaTypeByCodingSchemaTypeId.name';
    pagedFilterDTO.value = procedureSchema;
    fieldMap['codingSchemaTypeByCodingSchemaTypeId'] = pagedFilterDTO;

    pagedRequestDTO.fieldMap = fieldMap;
    return pagedRequestDTO;
  }

  public static getListOfCodingSchemaByCodePagedRequestDTO(value: string, procedureSchema: string, pageSize = 50) {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblCodingSchema;
    pagedRequestDTO.pageNumber = 0;
    pagedRequestDTO.pageSize = pageSize;

    const fieldMap: {} = createFieldMap(
      'code',
      value
    );

    const pagedFilterDTO = new PagedFilterDTO();
    pagedFilterDTO.name = 'codingSchemaTypeByCodingSchemaTypeId.name';
    pagedFilterDTO.value = procedureSchema;
    fieldMap['codingSchemaTypeByCodingSchemaTypeId'] = pagedFilterDTO;

    pagedRequestDTO.fieldMap = fieldMap;
    return pagedRequestDTO;
  }


  // Note: Can be used for dual purpose:
  // MatPaginator Controls
  // TableControl Fields

  public static getTableControls(length = 100,
                                 pageSize = 10,
                                 pageNumber = 0,
                                 pageSizeOptions = [5, 10, 25, 100]) {
    return {
      length,
      pageSize,
      pageNumber,
      pageSizeOptions
    };
  }
}
